import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Chip,
} from '@mui/material';
import { ArrowBack, CheckCircle, AddPhotoAlternate, Close, Warning } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubmitAppeal } from '../../hooks/useAppeal';
import type { SanctionType } from '../../types/report';

const SANCTION_LABELS: Record<SanctionType, string> = {
  WARNING: '1단계 경고',
  SUSPENSION: '7일 이용 정지',
  BAN: '영구 차단',
};

const AppealFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sanctionIdParam = searchParams.get('sanctionId') ?? '';
  const sanctionTypeParam = (searchParams.get('sanctionType') as SanctionType | null) ?? null;

  const [sanctionId, setSanctionId] = useState(sanctionIdParam);
  const [sanctionType, setSanctionType] = useState<SanctionType | ''>(sanctionTypeParam ?? '');
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const { submit, loading, error } = useSubmitAppeal();

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files ?? []);
    const remaining = 5 - files.length;
    const toAdd = added.slice(0, remaining);
    setFiles((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setFilePreviews((prev) => [...prev, ev.target?.result as string]);
        reader.readAsDataURL(file);
      } else {
        setFilePreviews((prev) => [...prev, '']);
      }
    });
    e.target.value = '';
  };

  const handleFileRemove = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFilePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!sanctionId || !sanctionType || !reason.trim()) return;
    const result = await submit({
      sanctionId: Number(sanctionId),
      sanctionType,
      reason: reason.trim(),
      attachments: files.length > 0 ? files : undefined,
    });
    if (result) setSubmitted(true);
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>이의제기</Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>이의제기가 접수되었습니다</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '1rem', lineHeight: 1.8 }}>
            7영업일 이내 심사 결과를 안내드리겠습니다.
            <br />
            심사 중에도 이용 제한 상태는 유지됩니다.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700, mb: 1.5 }}
            onClick={() => navigate('/appeal/list')}
          >
            이의제기 내역 보기
          </Button>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            sx={{ py: 1.5, fontSize: '1rem' }}
            onClick={() => navigate('/support')}
          >
            고객센터 홈
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>이의제기</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* 기한 안내 */}
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'warning.light', border: '1px solid', borderColor: 'warning.main', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
        >
          <Warning sx={{ color: 'warning.dark', mt: 0.25, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.95rem', color: 'warning.dark', lineHeight: 1.6 }}>
            제한 통지일로부터 <strong>14일 이내</strong> 접수 가능합니다.
            기한 경과 시 이의제기가 불가합니다.
          </Typography>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* 제재 유형 */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ fontSize: '1rem' }}>제재 유형 *</InputLabel>
          <Select
            value={sanctionType}
            label="제재 유형 *"
            onChange={(e) => setSanctionType(e.target.value as SanctionType)}
            sx={{ fontSize: '1rem' }}
          >
            {(Object.keys(SANCTION_LABELS) as SanctionType[]).map((type) => (
              <MenuItem key={type} value={type} sx={{ fontSize: '1rem' }}>
                {SANCTION_LABELS[type]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 제재 ID (URL 파라미터 없을 경우 입력) */}
        {!sanctionIdParam && (
          <TextField
            fullWidth
            label="제재 ID *"
            placeholder="제재 통보 메시지에서 확인하세요"
            value={sanctionId}
            onChange={(e) => setSanctionId(e.target.value.trim())}
            inputProps={{ style: { fontSize: '1rem' } }}
            sx={{ mb: 3 }}
          />
        )}

        {/* 이의 사유 */}
        <TextField
          fullWidth
          multiline
          rows={7}
          label="이의 사유 *"
          placeholder="이의를 제기하는 사유를 상세히 입력해주세요&#10;&#10;예: 해당 신고 내용은 사실과 다르며, 저는 어떠한 부적절한 행위도 하지 않았습니다."
          value={reason}
          onChange={(e) => { if (e.target.value.length <= 2000) setReason(e.target.value); }}
          inputProps={{ style: { fontSize: '1rem' } }}
          helperText={`${reason.length}/2000`}
          FormHelperTextProps={{ sx: { textAlign: 'right' } }}
          sx={{ mb: 3 }}
        />

        {/* 증빙 자료 */}
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>
          증빙 자료{' '}
          <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.9rem' }}>
            (선택, 이미지·PDF 최대 5장)
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {files.map((file, idx) => (
            <Box key={idx} sx={{ position: 'relative', width: 80, height: 80 }}>
              {filePreviews[idx] ? (
                <img
                  src={filePreviews[idx]}
                  alt={file.name}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                />
              ) : (
                <Paper
                  elevation={0}
                  sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                >
                  <Typography sx={{ fontSize: '0.65rem', textAlign: 'center', color: 'text.secondary', px: 0.5, wordBreak: 'break-all' }}>
                    {file.name}
                  </Typography>
                </Paper>
              )}
              <IconButton
                size="small"
                onClick={() => handleFileRemove(idx)}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: 0.25, border: '1px solid', borderColor: 'divider' }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {files.length < 5 && (
            <label>
              <input type="file" accept="image/*,application/pdf" multiple hidden onChange={handleFileAdd} />
              <Box
                sx={{
                  width: 80, height: 80, border: '1px dashed', borderColor: 'divider',
                  borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <AddPhotoAlternate sx={{ color: 'text.secondary' }} />
              </Box>
            </label>
          )}
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!sanctionId || !sanctionType || !reason.trim() || loading}
          onClick={handleSubmit}
          sx={{ py: 1.75, fontSize: '1.05rem', fontWeight: 700 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : '이의제기 접수하기'}
        </Button>

        <Typography sx={{ mt: 2, fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center', lineHeight: 1.6 }}>
          동일 제재 건에 대한 재이의는 1회만 가능합니다.
          <br />
          문의하기로도 의견을 전달하실 수 있습니다.
        </Typography>
      </Container>
    </Box>
  );
};

export default AppealFormPage;
